import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ExternalLink } from "lucide-react";

// Portfolio website screenshots data
const portfolioSites = [
  {
    name: "Zorba's Greek Kitchen",
    type: "Restaurant",
    image: "/portfolio/zorbas-greek.png",
  },
  {
    name: "Apex Plumbing",
    type: "Service Business",
    image: "/portfolio/apex-plumbing.png",
  },
  {
    name: "Los Generales",
    type: "Mexican Restaurant",
    image: "/portfolio/los-generales.png",
  },
  {
    name: "Mama Tana",
    type: "Salvadoran Restaurant",
    image: "/portfolio/mama-tana.png",
  },
  {
    name: "Taqueria La Fiesta",
    type: "Mexican Street Food",
    image: "/portfolio/taqueria-la-fiesta.png",
  },
  {
    name: "Pretzel Bakery",
    type: "Bakery",
    image: "/portfolio/pretzels-bakery.png",
  },
];

// Lightbox Modal Component
const Lightbox = ({ 
  site, 
  onClose,
  onNext,
  onPrev,
}: { 
  site: typeof portfolioSites[0] | null; 
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev]);

  if (!site) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md p-4 md:p-8"
    >
      {/* Navigation arrows */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-5xl w-full max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-2xl"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Image */}
        <div className="overflow-auto max-h-[calc(85vh-72px)]">
          <img
            src={site.image}
            alt={`${site.name} website screenshot`}
            className="w-full h-auto"
          />
        </div>

        {/* Info bar */}
        <div className="p-4 border-t border-white/10 bg-[#0a0a0a] flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold text-lg">{site.name}</h3>
            <p className="text-white/50 text-sm">{site.type}</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors">
            <ExternalLink className="w-4 h-4" />
            View Site
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const PortfolioShowcase = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSite, setSelectedSite] = useState<typeof portfolioSites[0] | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const goToSlide = useCallback((index: number) => {
    const newIndex = (index + portfolioSites.length) % portfolioSites.length;
    setActiveIndex(newIndex);
  }, []);

  const nextSlide = useCallback(() => goToSlide(activeIndex + 1), [activeIndex, goToSlide]);
  const prevSlide = useCallback(() => goToSlide(activeIndex - 1), [activeIndex, goToSlide]);

  // Handle swipe gestures
  const handleDragEnd = useCallback((event: any, info: { offset: { x: number }; velocity: { x: number } }) => {
    const swipeThreshold = 50;
    const velocityThreshold = 500;
    
    if (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) {
      nextSlide();
    } else if (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) {
      prevSlide();
    }
    setIsDragging(false);
  }, [nextSlide, prevSlide]);

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || selectedSite || isDragging) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide, selectedSite, isDragging]);

  const openLightbox = (site: typeof portfolioSites[0]) => {
    setSelectedSite(site);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedSite(null);
    document.body.style.overflow = '';
  };

  const lightboxNext = () => {
    const currentIdx = portfolioSites.findIndex(s => s.name === selectedSite?.name);
    const nextIdx = (currentIdx + 1) % portfolioSites.length;
    setSelectedSite(portfolioSites[nextIdx]);
  };

  const lightboxPrev = () => {
    const currentIdx = portfolioSites.findIndex(s => s.name === selectedSite?.name);
    const prevIdx = (currentIdx - 1 + portfolioSites.length) % portfolioSites.length;
    setSelectedSite(portfolioSites[prevIdx]);
  };

  // Get visible cards (previous, current, next)
  const getCardStyle = (index: number) => {
    const diff = index - activeIndex;
    const wrappedDiff = ((diff + portfolioSites.length + Math.floor(portfolioSites.length / 2)) % portfolioSites.length) - Math.floor(portfolioSites.length / 2);
    
    if (wrappedDiff === 0) {
      return {
        x: 0,
        scale: 1,
        rotateY: 0,
        zIndex: 10,
        opacity: 1,
        filter: "brightness(1)",
      };
    } else if (wrappedDiff === 1 || wrappedDiff === -portfolioSites.length + 1) {
      return {
        x: "60%",
        scale: 0.85,
        rotateY: -15,
        zIndex: 5,
        opacity: 0.7,
        filter: "brightness(0.6)",
      };
    } else if (wrappedDiff === -1 || wrappedDiff === portfolioSites.length - 1) {
      return {
        x: "-60%",
        scale: 0.85,
        rotateY: 15,
        zIndex: 5,
        opacity: 0.7,
        filter: "brightness(0.6)",
      };
    } else if (wrappedDiff === 2 || wrappedDiff === -portfolioSites.length + 2) {
      return {
        x: "110%",
        scale: 0.7,
        rotateY: -25,
        zIndex: 2,
        opacity: 0.4,
        filter: "brightness(0.4)",
      };
    } else if (wrappedDiff === -2 || wrappedDiff === portfolioSites.length - 2) {
      return {
        x: "-110%",
        scale: 0.7,
        rotateY: 25,
        zIndex: 2,
        opacity: 0.4,
        filter: "brightness(0.4)",
      };
    }
    return {
      x: wrappedDiff > 0 ? "150%" : "-150%",
      scale: 0.5,
      rotateY: wrappedDiff > 0 ? -30 : 30,
      zIndex: 1,
      opacity: 0,
      filter: "brightness(0.3)",
    };
  };

  return (
    <div>
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {t("dashboard.portfolioTitle")}
        </h2>
        <p className="text-lg text-white/40">
          {t("dashboard.portfolioDescription")}
        </p>
      </div>

      {/* 3D Spotlight Carousel */}
      <div 
        className="relative h-[320px] md:h-[420px] overflow-hidden"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
        style={{ perspective: "1200px" }}
      >
        {/* Swipe gesture layer for mobile */}
        <motion.div
          className="absolute inset-0 z-30 md:hidden touch-pan-y"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={handleDragEnd}
        />

        {/* Cards container */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none md:pointer-events-auto">
          {portfolioSites.map((site, index) => {
            const style = getCardStyle(index);
            const isActive = index === activeIndex;
            
            return (
              <motion.div
                key={site.name}
                animate={{
                  x: style.x,
                  scale: style.scale,
                  rotateY: style.rotateY,
                  zIndex: style.zIndex,
                  opacity: style.opacity,
                }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  opacity: { duration: 0.2 }
                }}
                onClick={() => {
                  if (isDragging) return;
                  isActive ? openLightbox(site) : goToSlide(index);
                }}
                className="absolute w-[280px] md:w-[480px] cursor-pointer pointer-events-auto"
                style={{ 
                  transformStyle: "preserve-3d",
                  filter: style.filter,
                }}
              >
                <div className={`
                  relative overflow-hidden rounded-xl border transition-all duration-300
                  ${isActive 
                    ? 'border-indigo-500/50 shadow-[0_0_60px_rgba(99,102,241,0.3)]' 
                    : 'border-white/10'
                  }
                `}>
                  <div className="aspect-[16/10] overflow-hidden bg-[#111]">
                    <img
                      src={site.image}
                      alt={`${site.name} website screenshot`}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  
                  {/* Text content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <h4 className="text-white font-semibold text-base md:text-lg drop-shadow-lg">{site.name}</h4>
                    <p className="text-white/60 text-xs md:text-sm">{site.type}</p>
                  </div>

                  {/* Click to expand hint for active card */}
                  {isActive && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute top-4 right-4"
                    >
                      <span className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-medium flex items-center gap-1.5">
                        <ExternalLink className="w-3 h-3" />
                        Click to expand
                      </span>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        
        <button
          onClick={nextSlide}
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-6">
        {portfolioSites.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`
              h-2 rounded-full transition-all duration-300
              ${index === activeIndex 
                ? 'w-8 bg-indigo-500' 
                : 'w-2 bg-white/20 hover:bg-white/40'
              }
            `}
          />
        ))}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedSite && (
          <Lightbox 
            site={selectedSite} 
            onClose={closeLightbox}
            onNext={lightboxNext}
            onPrev={lightboxPrev}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
